import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import "./SyntheticResult.scss";
import CollapsableCard from "../../../common/components/CollapsableCard";
import ThermoGaugeChart from "./ThermoGaugeChart";
import {
  DEFAULT_ACTIVE_COLOR,
  selectStyles,
} from "../../../common/constants/color";
import { AFFICHAGERSLT, ETAPESRS, INDICATEURRS } from "../constants/metadata";
import LabelChart from "./LabelChart";
import ExposantValue from "./ExposantValue";
import CustomTooltip from "../../../common/components/CustomTooltip";
import { unitRegistry } from "../utils/unitRegistry";

const SyntheticResult = ({
  steps,
  additionalResults,
  localConfigurator,
  indicators,
  onChangeSyntheticResultForm,
  indicatorValue,
  stepValue,
  result,
  measure,
  metadata,
  excludedCouples,
  fdesParameters,
  fdesPerUnit,
}) => {
  const activeColor =
    localConfigurator &&
    localConfigurator.theme &&
    localConfigurator.theme.colors &&
    localConfigurator.theme.colors.primaryText
      ? localConfigurator.theme.colors.primaryText
      : DEFAULT_ACTIVE_COLOR;

  const [indicateursRs, setIndicateursRs] = useState({});
  const [etapesRs, setEtapesRs] = useState({});
  const [affichageRslt, setAffichageRslt] = useState(null);
  const [filteredSteps, setFilteredSteps] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [perUnit, setPerUnit] = useState("");

  const normalizedDisplayType = (affichageRslt?.defaultValue || "")
    .toString()
    .trim()
    .toLowerCase();
  const displayType = ["thermometer", "label", "value"].includes(
    normalizedDisplayType,
  )
    ? normalizedDisplayType
    : "thermometer";
  const noteTextileResult = additionalResults?.find(
    (x) => x.name === "Note_textile",
  );

  useEffect(() => {
    metadata &&
      setIndicateursRs(metadata.find((e) => e.paramId === INDICATEURRS));
    metadata && setEtapesRs(metadata.find((e) => e.paramId === ETAPESRS));
    metadata &&
      setAffichageRslt(metadata.find((e) => e.paramId === AFFICHAGERSLT));
  }, [metadata]); //eslint-disable-line

  useEffect(() => {
    if (fdesPerUnit && fdesPerUnit !== "") {
      setPerUnit(fdesPerUnit);
      return;
    }
    if (fdesParameters && fdesParameters.length > 0) {
      const unitParam = fdesParameters.find(
        (param) => param.paramId === "UNITID",
      );
      if (unitParam) {
        setPerUnit(
          unitRegistry.find(
            (u) => u.unitId.toString() === unitParam.defaultValue.toString(),
          )?.unit || "",
        );
      }
    }
  }, [fdesParameters, fdesPerUnit]); //eslint-disable-line

  useEffect(() => {
    if (indicatorValue?.value && excludedCouples.length > 0) {
      const stepsToDelete = [];
      excludedCouples.forEach((couple) => {
        if (couple.indicator === indicatorValue.value)
          stepsToDelete.push(couple.step);
      });
      const newSteps = [
        ...steps.filter((step) => !stepsToDelete.includes(step.id)),
      ];
      setFilteredSteps(newSteps.filter((step) => step.type === "Step"));
      setFilteredModules(
        newSteps.filter(
          (step) => step.type === "Module" || step.type === "Total",
        ),
      );
      if (newSteps.filter((step) => step.id === stepValue.value).length === 0) {
        onChangeSyntheticResultForm("step", {
          value: newSteps[0].id,
          label: `${newSteps[0].acronym} - ${newSteps[0].title}`,
        });
      }
    } else {
      setFilteredSteps(steps.filter((step) => step.type === "Step"));
      setFilteredModules(
        steps.filter((step) => step.type === "Module" || step.type === "Total"),
      );
    }
  }, [indicatorValue, excludedCouples]); //eslint-disable-line

  return (
    <CollapsableCard title="Résultats synthétiques">
      {(displayType === "thermometer" || displayType === "value") && (
        <div className="row">
          <div className="col-7">
            <fieldset className="form-group">
              <label htmlFor="indicators">
                {indicateursRs &&
                  (indicateursRs.name
                    ? indicateursRs.name
                    : "Indicateur environnemental")}{" "}
                <i
                  className="feather icon-info ml-1"
                  data-tooltip-content={
                    indicateursRs &&
                    (indicateursRs.helpText
                      ? indicateursRs.helpText
                      : "Sélectionnez l’indicateur environnemental pour lequel vous souhaitez afficher le résultat. Les indicateurs proposés sont ceux requis en France pour la réalisation de déclarations environnementales de produits et équipements de construction, et la réalisation d’analyses de cycle de vie de bâtiments.")
                  }
                  data-tooltip-id="indicators"
                />
              </label>
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles(activeColor)}
                value={indicatorValue}
                onChange={(selectedOption) =>
                  onChangeSyntheticResultForm("indicator", selectedOption)
                }
                options={
                  indicators &&
                  indicators.map((indicator) => {
                    return { value: indicator.id, label: indicator.title };
                  })
                }
              />
              <CustomTooltip id="indicators" place="top" />
            </fieldset>
            <fieldset className="form-group">
              <label htmlFor="indicators">
                {etapesRs &&
                  (etapesRs.name
                    ? etapesRs.name
                    : "Étape ou module du cycle de vie")}{" "}
                <i
                  className="feather icon-info ml-1"
                  data-tooltip-content={
                    etapesRs &&
                    (etapesRs.helpText
                      ? etapesRs.helpText
                      : "Sélectionnez l’étape ou le module du cycle de vie pour lequel vous souhaitez afficher le résultat. Les étapes et modules proposés sont ceux requis en France pour la réalisation de déclarations environnementales de produits et équipements de construction, et la réalisation d’analyses de cycle de vie de bâtiments.")
                  }
                  data-tooltip-id="indicators"
                />
              </label>
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles(activeColor)}
                value={stepValue}
                onChange={(selectedOption) =>
                  onChangeSyntheticResultForm("step", selectedOption)
                }
                options={[
                  {
                    label: "Étapes du cycle de vie",
                    options: (filteredSteps || []).map((step) => {
                      return {
                        value: step.id,
                        label: `${step.acronym} - ${step.title}`,
                      };
                    }),
                  },
                  {
                    label: "Modules du cycle de vie",
                    options: (filteredModules || []).map((step) => {
                      return {
                        value: step.id,
                        label: `${step.acronym} - ${step.title}`,
                      };
                    }),
                  },
                ]}
                components={(props) => {
                  return { Group: <components.Group {...props} /> };
                }}
              />
              <CustomTooltip id="indicators" place="top" />
            </fieldset>
          </div>
          <div className="col-5">
            {displayType === "thermometer" &&
              result &&
              result.value &&
              result.measurements && (
                <ThermoGaugeChart
                  value={result.value}
                  measure={measure}
                  measurements={result.measurements}
                  helpText={affichageRslt?.helpText || null}
                  perUnit={perUnit}
                />
              )}
            {displayType === "value" && result && result.value && (
              <div className="flex-center h-100">
                <span className="synth-rslt-value">
                  <ExposantValue value={result?.value} forceLiteral />
                  <span className="measure">{measure}</span>
                  {perUnit && <span className="measure">(par {perUnit})</span>}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      {displayType === "label" && (
        <div className="row">
          <div className="col-12">
            {noteTextileResult && (
              <LabelChart
                value={noteTextileResult.value}
                helpText={affichageRslt?.helpText || null}
              />
            )}
            {!noteTextileResult && (
              <div className="alert alert-warning mb-0">
                Affichage en mode label actif, mais la Note{" "}
                {/* valeur "Note_textile" */}
                est absente des résultats.
              </div>
            )}
          </div>
        </div>
      )}
    </CollapsableCard>
  );
};

export default SyntheticResult;
